import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Typography, Paper, Grid, Card, CardContent, Button,
  CircularProgress, Divider, List, ListItem, ListItemText, ListItemIcon
} from '@mui/material';
import { Link } from 'react-router-dom';
import Breadcrumbs from '../../components/layout/Breadcrumbs';
import LocalShippingIcon from '@mui/icons-material/LocalShipping';
import KeyboardReturnIcon from '@mui/icons-material/KeyboardReturn';
import RoomIcon from '@mui/icons-material/Room';
import WarningIcon from '@mui/icons-material/Warning';
import InventoryIcon from '@mui/icons-material/Inventory';
import FormatListNumberedIcon from '@mui/icons-material/FormatListNumbered';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import ChecklistIcon from '@mui/icons-material/Checklist';
import ShoppingBagIcon from '@mui/icons-material/ShoppingBag';
import TimelineIcon from '@mui/icons-material/Timeline';
import RefreshIcon from '@mui/icons-material/Refresh';
import DashboardAPI from '../../services/dashboardapi.js';

const DASHBOARD_CACHE_KEY = 'dashboardData';
const DASHBOARD_TIMESTAMP_KEY = 'dashboardLastFetched';

const Dashboard = () => {
  const [loading, setLoading] = useState(true);
  const [dashboardData, setDashboardData] = useState({
    summary: {
      total_products: 0,
      available_variants: 0,
      low_stock_variants: 0,
      incomplete_variants: 0
    },
    top_selling: [],
    recently_updated: [],
    category_distribution: [],
    recent_transactions: [],
    incomplete_variants_list: []
  });
  const [error, setError] = useState(null);
  const [lastFetchTime, setLastFetchTime] = useState(null);

  // Function to fetch dashboard data with localStorage caching
  const fetchDashboardData = useCallback(async (forceRefresh = false) => {
    // Try to load from localStorage if not forcing refresh
    if (!forceRefresh) {
      try {
        const cachedData = localStorage.getItem(DASHBOARD_CACHE_KEY);
        const cachedTimestamp = localStorage.getItem(DASHBOARD_TIMESTAMP_KEY);
        
        if (cachedData && cachedTimestamp) {
          const parsedData = JSON.parse(cachedData);
          const fetchTime = new Date(Number(cachedTimestamp));
          
          setDashboardData(parsedData);
          setLastFetchTime(fetchTime);
          setLoading(false);
          return;
        }
      } catch (err) {
        console.error('Error reading from localStorage:', err);
        // Continue with API fetch if localStorage fails
      }
    }
    
    setLoading(true);
    try {
      // Use the DashboardAPI service
      const data = await DashboardAPI.getDashboardMetrics();
      setDashboardData(data);
      
      const currentTime = new Date();
      setLastFetchTime(currentTime);
      
      // Save to localStorage
      try {
        localStorage.setItem(DASHBOARD_CACHE_KEY, JSON.stringify(data));
        localStorage.setItem(DASHBOARD_TIMESTAMP_KEY, currentTime.getTime().toString());
      } catch (storageErr) {
        console.error('Error saving to localStorage:', storageErr);
        // Continue even if localStorage save fails
      }
      
      setError(null);
    } catch (err) {
      console.error('Error fetching dashboard data:', err);
      setError('Failed to load dashboard data. Please try again later.');
    } finally {
      setLoading(false);
    }
  }, []);

  // Handle manual refresh when button is clicked
  const handleRefresh = () => {
    fetchDashboardData(true); // Force refresh
  };

  useEffect(() => {
    // Initial fetch - will use localStorage if available
    fetchDashboardData(false);
  }, [fetchDashboardData]);

  return (
    <Box sx={{ padding: 3 }}>
      <Breadcrumbs items={[
        { label: 'Dashboard' }
      ]} />

      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h5" component="h1" sx={{ fontWeight: 500, color: '#333' }}>
          Dashboard
          {lastFetchTime && (
            <Typography variant="caption" sx={{ display: 'block', color: 'text.secondary', mt: 0.5 }}>
              Last updated: {lastFetchTime.toLocaleString()}
            </Typography>
          )}
        </Typography>
        <Button
          variant="outlined"
          color="primary"
          size="small"
          startIcon={<RefreshIcon />}
          onClick={handleRefresh}
          disabled={loading}
          sx={{
            textTransform: 'none',
            borderRadius: '20px',
            px: 2
          }}
        >
          {loading ? 'Refreshing...' : 'Refresh Data'}
        </Button>
      </Box>

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress />
        </Box>
      ) : error ? (
        <Paper sx={{ p: 3, mb: 3, bgcolor: '#ffebee' }}>
          <Typography color="error">{error}</Typography>
        </Paper>
      ) : (
        <>
          {/* Summary Metrics Cards */}
          {/* Top colored bars with metrics */}
          <Box sx={{ display: { md: 'flex', xs: 'none' }, width: '100%', mb: 3, }}>
            {/* Green bar - Total Products */}
            <Box sx={{ width: '25%', display: 'flex', flexDirection: 'column', borderRight: '4px solid #fff', bgcolor: '#fff' }}>
              <Box sx={{ width: '100%', height: '8px', bgcolor: '#4caf50', mb: 2 }} />
              <Box sx={{ display: 'flex', flexDirection: 'column', px: 1, }}>
                <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
                  Total Products <InventoryIcon sx={{ fontSize: '1rem', color: '#4caf50', ml: 1, verticalAlign: 'text-bottom' }} />
                </Typography>
                <Typography variant="h3" component={Link} to="/products" sx={{
                  fontWeight: 'bold',
                  textDecoration: 'none',
                  color: 'text.primary',
                  '&:hover': { color: '#4caf50' }
                }}>
                  {dashboardData.summary.total_products}
                </Typography>
              </Box>
            </Box>

            {/* Blue bar - Available Variants */}
            <Box sx={{ width: '25%', display: 'flex', flexDirection: 'column', borderRight: '4px solid #fff', bgcolor: '#fff', mx: 1 }}>
              <Box sx={{ width: '100%', height: '8px', bgcolor: '#2196f3', mb: 2 }} />
              <Box sx={{ display: 'flex', flexDirection: 'column', px: 1 }}>
                <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
                  Available Variants <FormatListNumberedIcon sx={{ fontSize: '1rem', color: '#2196f3', ml: 1, verticalAlign: 'text-bottom' }} />
                </Typography>
                <Typography variant="h3" component={Link} to="/variants" sx={{
                  fontWeight: 'bold',
                  textDecoration: 'none',
                  color: 'text.primary',
                  '&:hover': { color: '#2196f3' }
                }}>
                  {dashboardData.summary.available_variants}
                </Typography>
              </Box>
            </Box>

            {/* Orange bar - Low Stock Items */}
            <Box sx={{ width: '25%', display: 'flex', flexDirection: 'column', borderRight: '4px solid #fff', bgcolor: '#fff', mx: 1 }}>
              <Box sx={{ width: '100%', height: '8px', bgcolor: '#ff9800', mb: 2 }} />
              <Box sx={{ display: 'flex', flexDirection: 'column', px: 1 }}>
                <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
                  Low Stock Items <WarningIcon sx={{ fontSize: '1rem', color: '#ff9800', ml: 1, verticalAlign: 'text-bottom' }} />
                </Typography>
                <Typography variant="h3" component={Link} to="/variants?ordering=stock&state=active" sx={{
                  fontWeight: 'bold',
                  textDecoration: 'none',
                  color: 'text.primary',
                  '&:hover': { color: '#ff9800' }
                }}>
                  {dashboardData.summary.low_stock_variants}
                </Typography>
              </Box>
            </Box>

            {/* Red bar - Incomplete Variants */}
            <Box sx={{ width: '25%', display: 'flex', flexDirection: 'column', bgcolor: '#fff', mx: 1 }}>
              <Box sx={{ width: '100%', height: '8px', bgcolor: '#f44336', mb: 2 }} />
              <Box sx={{ display: 'flex', flexDirection: 'column', px: 1 }}>
                <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
                  Incomplete Variants <ErrorOutlineIcon sx={{ fontSize: '1rem', color: '#f44336', ml: 1, verticalAlign: 'text-bottom' }} />
                </Typography>
                <Typography variant="h3" sx={{
                  fontWeight: 'bold',
                  color: 'text.primary',
                }}>
                  {dashboardData.summary.incomplete_variants}
                </Typography>
              </Box>
            </Box>
          </Box>

          <Box mt={4}>
            {/* Quick Actions */}
            <Paper sx={{ p: 2, mb: 3 }}>
              <Typography variant="h6" gutterBottom sx={{ fontWeight: 500 }}>
                Quick Actions
              </Typography>

              <Grid container spacing={2}>
                {/* Sale & Return */}
                <Grid item xs={12}>
                  <Button
                    fullWidth
                    variant="outlined"
                    component={Link}
                    to="/sale-return"
                    sx={{
                      justifyContent: 'flex-start',
                      textTransform: 'none',
                      p: 1.5,
                      borderRadius: 2,
                      bgcolor: '#f5faff',
                      '&:hover': { bgcolor: '#e3f2fd' },
                    }}
                    startIcon={
                      <Box
                        sx={{
                          bgcolor: '#e3f2fd',
                          borderRadius: '50%',
                          width: 36,
                          height: 36,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                        }}
                      >
                        <LocalShippingIcon sx={{ color: '#1565c0', fontSize: 20 }} />
                      </Box>
                    }
                  >
                    <Box sx={{ ml: 1 }}>
                      <Typography variant="subtitle1" sx={{ fontWeight: 500 }}>
                        Sale & Return
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        Record sales and returns.
                      </Typography>
                    </Box>
                  </Button>
                </Grid>

                {/* Pick List */}
                <Grid item xs={12}>
                  <Button
                    fullWidth
                    variant="outlined"
                    component={Link}
                    to="/inventory/pick-list"
                    sx={{
                      justifyContent: 'flex-start',
                      textTransform: 'none',
                      p: 1.5,
                      borderRadius: 2,
                      bgcolor: '#f5fbf6',
                      '&:hover': { bgcolor: '#e8f5e9' },
                    }}
                    startIcon={
                      <Box
                        sx={{
                          bgcolor: '#e8f5e9',
                          borderRadius: '50%',
                          width: 36,
                          height: 36,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                        }}
                      >
                        <ChecklistIcon sx={{ color: '#2e7d32', fontSize: 20 }} />
                      </Box>
                    }
                  >
                    <Box sx={{ ml: 1 }}>
                      <Typography variant="subtitle1" sx={{ fontWeight: 500 }}>
                        Pick List
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        Items to pick for orders.
                      </Typography>
                    </Box>
                  </Button>
                </Grid>

                {/* Product Location */}
                <Grid item xs={12}>
                  <Button
                    fullWidth
                    variant="outlined"
                    component={Link}
                    to="/inventory/location"
                    sx={{
                      justifyContent: 'flex-start',
                      textTransform: 'none',
                      p: 1.5,
                      borderRadius: 2,
                      bgcolor: '#fef9f5',
                      '&:hover': { bgcolor: '#fff3e0' },
                    }}
                    startIcon={
                      <Box
                        sx={{
                          bgcolor: '#fff3e0',
                          borderRadius: '50%',
                          width: 36,
                          height: 36,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                        }}
                      >
                        <RoomIcon sx={{ color: '#ef6c00', fontSize: 20 }} />
                      </Box>
                    }
                  >
                    <Box sx={{ ml: 1 }}>
                      <Typography variant="subtitle1" sx={{ fontWeight: 500 }}>
                        Product Location
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        Find item storage locations.
                      </Typography>
                    </Box>
                  </Button>
                </Grid>
              </Grid>
            </Paper>

            {/* Recent Transactions */}
            <Grid container spacing={3} mb={3}>
              <Grid item xs={12} md={6}>
                <Paper sx={{ p: 3, height: '100%' }}>
                  <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center' }}>
                    <TimelineIcon sx={{ mr: 1 }} /> Recent Transactions
                  </Typography>
                  <Divider sx={{ mb: 2 }} />

                  {dashboardData.recent_transactions && dashboardData.recent_transactions.length > 0 ? (
                    <List>
                      {dashboardData.recent_transactions.map((transaction) => (
                        <ListItem
                          key={transaction.id}
                          divider
                          sx={{
                            '&:last-child': { borderBottom: 'none' }
                          }}
                        >
                          <ListItemText
                            primary={`${transaction.product_name} (${transaction.variant_sku})`}
                            secondary={
                              <React.Fragment>
                                <Typography component="span" variant="body2" color="text.primary">
                                  {transaction.transaction_type} • {transaction.quantity}
                                </Typography>
                                <Typography component="span" variant="body2" display="block" color="text.secondary">
                                  {new Date(transaction.timestamp).toLocaleString()} • {transaction.marketplace}
                                </Typography>
                              </React.Fragment>
                            }
                          />
                        </ListItem>
                      ))}
                    </List>
                  ) : (
                    <Typography variant="body2" color="text.secondary">
                      No recent transactions to display.
                    </Typography>
                  )}
                </Paper>
              </Grid>

              {/* Top Selling Products */}
              <Grid item xs={12} md={6}>
                <Paper sx={{ p: 3, height: '100%' }}>
                  <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center' }}>
                    <ShoppingBagIcon sx={{ mr: 1 }} /> Top Selling Products
                  </Typography>
                  <Divider sx={{ mb: 2 }} />

                  {dashboardData.top_selling && dashboardData.top_selling.length > 0 ? (
                    <List>
                      {dashboardData.top_selling.map((product, index) => (
                        <ListItem
                          key={product.sku}
                          divider={index < dashboardData.top_selling.length - 1}
                        >
                          <ListItemIcon>
                            {product.image ? (
                              <img
                                src={product.image}
                                alt={product.product_name}
                                style={{ width: 40, height: 40, objectFit: 'cover', borderRadius: 4 }}
                              />
                            ) : (
                              <Box sx={{
                                width: 40,
                                height: 40,
                                bgcolor: 'grey.200',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                borderRadius: 1
                              }}>
                                <InventoryIcon color="disabled" />
                              </Box>
                            )}
                          </ListItemIcon>
                          <ListItemText
                            primary={product.product_name}
                            secondary={`SKU: ${product.sku} • Sales: ${product.sales} • Stock: ${product.stock}`}
                          />
                        </ListItem>
                      ))}
                    </List>
                  ) : (
                    <Typography variant="body2" color="text.secondary">
                      No sales data available yet.
                    </Typography>
                  )}
                </Paper>
              </Grid>
            </Grid>

            {/* Incomplete Variants List */}
            {dashboardData.incomplete_variants_list && dashboardData.incomplete_variants_list.length > 0 && (
              <Paper sx={{ p: 3, mb: 3 }}>
                <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center' }}>
                  <ErrorOutlineIcon sx={{ mr: 1, color: '#f44336' }} /> Incomplete Variants
                </Typography>
                <Divider sx={{ mb: 2 }} />

                <List dense>
                  {dashboardData.incomplete_variants_list.map((variant) => (
                    <ListItem
                      key={variant.id}
                      divider
                      component={Link}
                      to={`/variants`}
                      sx={{
                        cursor: 'pointer',
                        '&:hover': { bgcolor: '#f5f5f5' }
                      }}
                    >
                      <ListItemText
                        primary={
                          <Box sx={{ display: 'flex', alignItems: 'center' }}>
                            <Typography variant="body1" component="span" fontWeight="medium">
                              {variant.sku}
                            </Typography>
                            <Typography variant="body2" component="span" color="text.secondary" sx={{ ml: 2 }}>
                              {variant.product_name}
                            </Typography>
                          </Box>
                        }
                        secondary={
                          <Typography variant="body2" color="error">
                            Missing: {variant.missing_fields.join(', ')}
                          </Typography>
                        }
                      />
                      <Typography variant="caption" color="text.secondary" sx={{ ml: 2 }}>
                        Updated: {new Date(variant.updated_at).toLocaleDateString()}
                      </Typography>
                    </ListItem>
                  ))}

                </List>
              </Paper>
            )}

            {/* Category Distribution */}
            <Paper sx={{ p: 3 }}>
              <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center' }}>
                <FormatListNumberedIcon sx={{ mr: 1 }} /> Category Distribution
              </Typography>
              <Divider sx={{ mb: 2 }} />

              {dashboardData.category_distribution && dashboardData.category_distribution.length > 0 ? (
                <Grid container spacing={2}>
                  {dashboardData.category_distribution.map((category) => (
                    <Grid item xs={6} sm={4} md={3} lg={2} key={category.name}>
                      <Card sx={{ bgcolor: '#f5f5f5' }}>
                        <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
                          <Typography variant="subtitle2" noWrap title={category.name}>
                            {category.name}
                          </Typography>
                          <Typography variant="h6" color="primary">
                            {category.count}
                          </Typography>
                        </CardContent>
                      </Card>
                    </Grid>
                  ))}
                </Grid>
              ) : (
                <Typography variant="body2" color="text.secondary">
                  No category data available.
                </Typography>
              )}
            </Paper>
          </Box>
        </>
      )}
    </Box>
  );
};

export default Dashboard;
